import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { FileText, ClipboardCheck, BookOpen, BarChart3, Crown, Sparkles, Lock, Lightbulb } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AIToolsDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // Not logged in, that's okay for this page
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const tools = [
    {
      id: 'worksheet',
      name: 'AI Worksheet Generator',
      description: 'Create differentiated Build • Grow • Shine worksheets.',
      icon: FileText,
      badge: 'FREE',
      badgeColor: 'bg-green-100 text-green-700',
      buttonText: 'Open Generator',
      route: '/ai-tools/worksheet-generator',
      requiresAuth: false,
      tier: 'free'
    },
    {
      id: 'testpaper',
      name: 'AI Test Paper Generator',
      description: 'Generate quizzes, assessments and printable test papers.',
      icon: ClipboardCheck,
      badge: 'FREE',
      badgeColor: 'bg-green-100 text-green-700',
      buttonText: 'Open Generator',
      route: '/ai-tools/test-generator',
      requiresAuth: false,
      tier: 'free'
    },
    {
      id: 'lesson',
      name: 'AI Lesson Planner',
      description: 'Generate complete lesson plans including objectives, activities, assessment, differentiation and homework.',
      icon: BookOpen,
      badge: 'Plus & Pro',
      badgeColor: 'bg-blue-100 text-blue-700',
      buttonText: 'Open Generator',
      route: '/ai-tools/lesson-planner',
      requiresAuth: true,
      tier: 'plus'
    },
    {
      id: 'activity',
      name: 'AI Activity Planner',
      description: 'Get 3 hands-on activity ideas using everyday household/classroom items. Perfect for budget-friendly, creative teaching.',
      icon: Lightbulb,
      badge: 'Plus & Pro',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      buttonText: 'Open Planner',
      route: '/ai-tools/activity-planner',
      requiresAuth: true,
      tier: 'plus'
    },
    {
      id: 'assessment',
      name: 'AI Assessment Hub',
      description: 'AI-assisted classroom assessment. Upload classroom assessments • AI reads answers • AI suggests marks • Teacher reviews final marks • Student reports • Assessment dashboard',
      icon: BarChart3,
      badge: 'Teacher Pro',
      badgeColor: 'bg-purple-100 text-purple-700',
      buttonText: 'Open Assessment Hub',
      route: '/assessment',
      requiresAuth: true,
      tier: 'pro'
    }
  ];

  const handleToolClick = (tool) => {
    if (tool.requiresAuth && !user) {
      navigate('/login');
      return;
    }

    if (tool.tier === 'plus' && user?.subscription_tier === 'free') {
      navigate('/pricing');
      return;
    }

    if (tool.tier === 'pro' && (user?.subscription_tier === 'free' || user?.subscription_tier === 'plus')) {
      navigate('/pricing');
      return;
    }

    navigate(tool.route);
  };

  const canAccess = (tool) => {
    if (!tool.requiresAuth) return true;
    if (!user) return false;
    
    if (tool.tier === 'free') return true;
    if (tool.tier === 'plus') return user.subscription_tier === 'plus' || user.subscription_tier === 'pro';
    if (tool.tier === 'pro') return user.subscription_tier === 'pro';
    
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Sparkles className="w-10 h-10 text-purple-600" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight font-bold text-slate-900">
                AI Tools
              </h1>
            </div>
            <p className="text-xl text-slate-700 font-medium mb-2">
              AI-powered teaching assistants built exclusively for teachers.
            </p>
            <p className="text-base text-slate-600">
              Helping Teachers Save Time • Helping Every Child Learn.
            </p>
          </div>

          {/* Tools Grid */}
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {tools.map((tool) => {
              const Icon = tool.icon;
              const hasAccess = canAccess(tool);
              
              return (
                <div
                  key={tool.id}
                  className="bg-white rounded-3xl border-2 border-slate-100 p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden group"
                >
                  {/* Background Gradient on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="relative">
                    {/* Icon & Badge */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-8 h-8 text-purple-600" />
                      </div>
                      
                      <span className={`px-4 py-1.5 rounded-full text-sm font-bold ${tool.badgeColor}`}>
                        {tool.badge}
                      </span>
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">
                      {tool.name}
                    </h3>
                    <p className="text-base text-slate-600 mb-6 leading-relaxed min-h-[4.5rem]">
                      {tool.description}
                    </p>

                    {/* Action Button */}
                    <button
                      onClick={() => handleToolClick(tool)}
                      className={`w-full py-4 rounded-xl font-semibold text-base transition-all duration-300 flex items-center justify-center gap-2 ${
                        hasAccess
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-md hover:shadow-xl active:scale-95'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                      disabled={!hasAccess && tool.requiresAuth}
                    >
                      {!hasAccess && tool.requiresAuth ? (
                        <>
                          <Lock className="w-5 h-5" />
                          Upgrade to Access
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          {tool.buttonText}
                        </>
                      )}
                    </button>

                    {/* Upgrade Hint */}
                    {!hasAccess && tool.requiresAuth && (
                      <p className="text-center text-sm text-slate-500 mt-3">
                        Requires {tool.badge} membership
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-8 text-center text-white shadow-2xl">
            <Crown className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-3">Unlock Premium AI Tools</h2>
            <p className="text-lg text-purple-100 mb-6 max-w-2xl mx-auto">
              Get unlimited access to AI Lesson Planner, Assessment Hub, and more with Teacher Plus or Teacher Pro membership.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="bg-white text-purple-600 hover:bg-purple-50 px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg hover:shadow-xl active:scale-95"
            >
              View Pricing Plans
            </button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default AIToolsDashboard;
